<template>
  <div
    id="layout"
    class="layout antialiased bg-white text-stone-900 font-sans border-b"
  >
    <client-only>
      <Sidebars />
    </client-only>

    <!-- Main + slot rendered first so page runs before header/footer and locale store is set for LocaleSwitcher -->
    <ErrorBoundary>
      <main class="layout__main">
        <div class="max-w-screen-xl m-auto px-4">
          <PageLoader />
          <slot />
        </div>
      </main>
    </ErrorBoundary>

    <header class="layout__header">
      <ErrorBoundary>
        <client-only>
          <Notifications />
        </client-only>
        <Navbar />
      </ErrorBoundary>
      <slot name="banner" />
    </header>

    <div class="layout__links">
      <slot name="links-section">
        <ErrorBoundary>
          <NuxtLazyHydrate when-visible>
            <EventListLinksBlock v-if="!isSimpleFooter" />
          </NuxtLazyHydrate>
        </ErrorBoundary>
      </slot>
    </div>
    <div class="layout__footer">
      <slot name="footer">
        <ErrorBoundary>
          <NuxtLazyHydrate when-visible>
            <Footer
              :footer-block-items="footerBlock"
              :display-newsletter-subscription-form="!isSimpleFooter"
            />
          </NuxtLazyHydrate>
        </ErrorBoundary>
      </slot>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { useNotificationsStore } from '../../stores/notifications'

const { locale } = useI18n()
const route = useRoute()
const { setupValidationPolling, deactivateValidationPolling } =
  useCartValidation()

const { data: footerData } = await useStrapiSingleType(
  `footer?locale=${locale.value}&populate[footerBlock][populate]=*`
)
const { footerBlock = [] } = footerData?.value?.data || {}

const isSimpleFooter = computed(() => route.meta.isSimpleFooter ?? false)

// Tracking notification height for CSS variable update
const notificationsStore = useNotificationsStore()
const { notificationHeight } = storeToRefs(notificationsStore)

// Update CSS variable when notification height changes
watch(
  notificationHeight,
  (height) => {
    if (import.meta.client) {
      document.documentElement.style.setProperty(
        '--notification-height',
        `${height}px`
      )
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await setupValidationPolling()
})

onUnmounted(async () => {
  await deactivateValidationPolling()
})

useLayoutHead()
</script>

<style lang="postcss" scoped>
.layout {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.layout__main {
  order: 2;
  position: relative;
  min-height: 60vh;
}

.layout__main:has(#home) {
  overflow: hidden;
}

.layout__header {
  order: 1;
  z-index: 18;
  @apply sticky top-0 md:z-20;
}

.layout__links {
  order: 3;
  z-index: 10;
}

.layout__footer {
  order: 4;
}
</style>
<style>
.Toastify__toast-container {
  max-width: 480px;
  --toastify-toast-width: max-content;
}
</style>
